import { createFileRoute, redirect } from '@tanstack/react-router';

import { SignUp } from '#modules/user/features/auth/components/SignUp';
import { safeCheckIsAuth } from '#modules/user/features/auth/utils/checkIsAuth';
import { appendNewUserFlag } from '#modules/user/features/onboarding/utils/appendNewUserFlag';
import { isNewUserFlag } from '#modules/user/features/onboarding/utils/isNewUserFlag';

export const Route = createFileRoute('/{-$locale}/_layout/sign-up')({
  component: RouteComponent,
  validateSearch: (
    search: Record<string, unknown>
  ): { provider?: string; redirect?: string; newUser?: string | boolean; msg?: string } => ({
    // provider/msg: OAuth callback params (spinner + error toast) — keep them or validateSearch
    // would strip them from the URL. redirect: post-auth return target. newUser: set by
    // SignUpForm right after a successful registration — see beforeLoad. It arrives as a string
    // from an in-app navigation and as a boolean when the URL itself is parsed (reload, direct
    // hit), so both shapes have to survive — dropping the boolean silently skipped onboarding.
    provider: typeof search.provider === 'string' ? search.provider : undefined,
    redirect: typeof search.redirect === 'string' ? search.redirect : undefined,
    newUser:
      typeof search.newUser === 'string' || typeof search.newUser === 'boolean'
        ? search.newUser
        : undefined,
    msg: typeof search.msg === 'string' ? search.msg : undefined,
  }),
  beforeLoad: async ({ location }) => {
    const { isAuth, status } = await safeCheckIsAuth();

    if (status !== 'ok' || !isAuth) {
      return;
    }

    // Read the search params off the route location — the old code reached for the global
    // `location`, whose `search` is a raw query string, so `.redirect` was always undefined.
    const search = location.search as { redirect?: string; newUser?: string | boolean };

    // A just-registered user lands on the profile section — settings is a list of sections on
    // a phone, and dropping a brand-new user on that list instead of the form he is meant to
    // fill in would cost him a tap for nothing. Anyone else who opens
    // /sign-up while signed in goes back where they came from. This guard owns the
    // post-registration redirect on purpose: it runs once the auth state has been refreshed,
    // so it cannot lose a race with a navigate() fired from the form.
    // Разбор значения, а не проверка «параметр есть»: строка `'false'` истинна, и на ней
    // возвращающийся пользователь проваливался в онбординг вместо своей страницы.
    if (isNewUserFlag(search.newUser)) {
      // Адрес возврата бьёт форму профиля: если человек пришёл за чем-то конкретным (приглашение
      // в сбор живёт только в адресе), туда он и должен вернуться. Метку новичка везём с собой —
      // попап онбординга висит в layout и всплывает на любой странице, а на приглашении
      // откладывает себя до следующего перехода.
      if (search.redirect) {
        throw redirect({ href: appendNewUserFlag(search.redirect) });
      }

      throw redirect({
        to: '/{-$locale}/dashboard/settings/profile',
        search: { newUser: 'true' },
      });
    }

    throw redirect({ to: search.redirect ?? '/dashboard' });
  },
  ssr: false,
});

function RouteComponent() {
  return <SignUp />;
}
